import { useEffect, useState } from "react";

/* Mobile-device detection for the desktop-only gate (OPSS-332). Capability, not
 * width: `pointer: coarse` + `hover: none` describe the primary input, so a
 * phone or tablet matches and a desktop browser never does — at any window
 * width, display scaling, or zoom level, all of which move CSS pixels around
 * and made the old `max-width: 768px` gate fire on half-screen desktops.
 * Deliberately diverges from studio-web's width-based useIsMobile. */
export const MOBILE_DEVICE_MEDIA_QUERY = "(pointer: coarse) and (hover: none)";

/** True on touch-primary devices; false when matchMedia is unavailable (jsdom). */
export function useIsMobileDevice(): boolean {
  const [isMobileDevice, setIsMobileDevice] = useState(
    () => window.matchMedia?.(MOBILE_DEVICE_MEDIA_QUERY).matches ?? false,
  );

  useEffect(() => {
    const mq = window.matchMedia?.(MOBILE_DEVICE_MEDIA_QUERY);
    if (!mq) return;

    // Re-read on mount: the query can flip between the initial state and the
    // effect (and StrictMode remounts every effect).
    setIsMobileDevice(mq.matches);

    const onChange = (event: MediaQueryListEvent) =>
      setIsMobileDevice(event.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return isMobileDevice;
}
